import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { EntitlementProvider } from './contexts/EntitlementContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './contexts/ToastContext';
import Layout from './components/Layout';
import { ImpersonationProvider } from './contexts/ImpersonationContext';
import SalesDashboard from './pages/SalesDashboard';
import EssentialsDashboard from './pages/Essentials/EssentialsDashboard';
import ContactsListPage from './components/essentials/ContactsListPage';
import EssentialsOr from './components/essentials/EssentialsOr';
import WebsiteIntegration from './pages/Essentials/WebsiteIntegration';
import EssentialsInbox from './pages/Essentials/EssentialsInbox';
import EssentialsTasks from './pages/Essentials/EssentialsTasks';
import Tasks from './pages/Tasks';
import Leads from './pages/Leads';
import LeadInboundDetail from './pages/LeadInboundDetail';
import Prospects from './pages/Prospects';
import Clients from './pages/Clients';
import LeadImports from './pages/LeadImports';
import LeadImportDetail from './pages/LeadImportDetail';
import TenantManagement from './pages/TenantManagement';
import WebsiteSettings from './pages/WebsiteSettings';
import ChatbotSettings from './pages/Chatbot/ChatbotSettings';
import ChatbotInstall from './pages/Chatbot/ChatbotInstall';
import ChatInbox from './pages/Chatbot/ChatInbox';
import ChatbotLeads from './pages/Chatbot/ChatbotLeads';
import EmailSettings from './pages/EmailSettings';
import MessagePerformance from './pages/MessagePerformance';
import Campaigns from './pages/Campaigns';
import CampaignDetail from './pages/CampaignDetail';
import Sequences from './pages/Sequences';
import SequenceEditor from './pages/SequenceEditor';
import UpsellPipeline from './pages/UpsellPipeline';
import AdminDashboard from './pages/Admin/AdminDashboard';
import AdminLogin from './pages/Admin/AdminLogin';
import PlatformAdminLayout from './pages/PlatformAdmin/PlatformAdminLayout';
import PlatformAdminLogin from './pages/PlatformAdmin/PlatformAdminLogin';
import PlatformAdminOverview from './pages/PlatformAdmin/Overview';
import PlatformAdminTenants from './pages/PlatformAdmin/Tenants';
import PlatformAdminWebsites from './pages/PlatformAdmin/Websites';
import PlatformAdminLeads from './pages/PlatformAdmin/Leads';
import PlatformAdminRevenue from './pages/PlatformAdmin/Revenue';
import PlatformAdminHealth from './pages/PlatformAdmin/Health';
import PlatformAdminPlans from './pages/PlatformAdmin/Plans';
import PlatformAdminAuditLogs from './pages/PlatformAdmin/AuditLogs';
import PlatformAdminUsers from './pages/PlatformAdmin/Users';
import PlatformAdminIntegrations from './pages/PlatformAdmin/Integrations';
import DailyLeadSuggestions from './pages/DailyLeadSuggestions';
import OnboardingLeadPreferences from './pages/OnboardingLeadPreferences';
import OnboardingVerticalPack from './pages/OnboardingVerticalPack';
import Login from './pages/Login';
import Register from './pages/Register';
import ChangePassword from './pages/ChangePassword';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import { subscriptionService } from './services/subscriptionService';
import { isMambaExemptTenantName } from './utils/tenantOverrides';
import './App.css';

const isBillingLocked = (
  status: string | undefined,
  trialEndsAt: string | null | undefined
) => {
  const normalized = (status || '').toLowerCase();
  if (normalized === 'past_due' || normalized === 'cancelled' || normalized === 'expired') {
    return true;
  }
  if (normalized === 'trial' && trialEndsAt) {
    const trialDate = new Date(trialEndsAt);
    if (!Number.isNaN(trialDate.getTime()) && trialDate.getTime() <= Date.now()) {
      return true;
    }
  }
  return false;
};

// Protected Route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();
  const isChangePasswordPage = location.pathname === '/change-password';
  const isTenantBillingPage = location.pathname === '/tenant';
  const [checkingBillingLock, setCheckingBillingLock] = useState(true);
  const [billingLocked, setBillingLocked] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const checkBillingLock = async () => {
      if (!isAuthenticated || !user) {
        if (!cancelled) {
          setBillingLocked(false);
          setCheckingBillingLock(false);
        }
        return;
      }

      if (isMambaExemptTenantName(user.tenant?.name)) {
        if (!cancelled) {
          setBillingLocked(false);
          setCheckingBillingLock(false);
        }
        return;
      }

      try {
        const subscription = await subscriptionService.getSubscription();
        if (!cancelled) {
          setBillingLocked(isBillingLocked(subscription.status, subscription.trial_ends_at));
        }
      } catch {
        if (!cancelled) {
          // If subscription lookup fails, fallback to unlocked and rely on backend 403 lockout checks.
          setBillingLocked(false);
        }
      } finally {
        if (!cancelled) {
          setCheckingBillingLock(false);
        }
      }
    };

    setCheckingBillingLock(true);
    checkBillingLock();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, user?.id, user?.tenant?.name]);

  if (loading || checkingBillingLock) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Force password reset: only allow /change-password
  if (user?.must_reset_password && !isChangePasswordPage) {
    return <Navigate to="/change-password" replace />;
  }

  if (billingLocked && !isTenantBillingPage && !isChangePasswordPage) {
    return <Navigate to="/tenant?tab=billing&locked=1" replace />;
  }

  return <>{children}</>;
};

// Public Route component (redirect to dashboard if already authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }
  
  return isAuthenticated ? <Navigate to="/" /> : <>{children}</>;
};

// Dashboard or redirect: admins see dashboard, others redirect to leads/prospects
const DashboardOrRedirect: React.FC = () => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-lg">Loading...</div>;
  if (user?.role === 'admin') return <SalesDashboard />;
  if (user?.role === 'sales') return <Navigate to="/leads" replace />;
  return <Navigate to="/prospects" replace />;
};

// Production admin host (Admin Dashboard must be accessed from this subdomain in production)
const ADMIN_HOST = typeof window !== 'undefined' ? (process.env.REACT_APP_ADMIN_HOST || 'admin.launchpointapp.com') : 'admin.launchpointapp.com';

// Admin protected route: redirect to /admin/login when unauthenticated (not main app /login)
const AdminProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-app">
        <div className="text-lg text-fg-muted">Loading...</div>
      </div>
    );
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/admin/login" replace />;
};

// Platform Admin route: superuser only, namespace /platform/admin/
const PlatformAdminProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-app">
        <div className="text-lg text-fg-muted">Loading...</div>
      </div>
    );
  }
  if (!isAuthenticated) {
    return <Navigate to="/platform/admin/login" replace />;
  }
  if (!user?.is_superuser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-app">
        <div className="bg-surface border border-border p-8 rounded-lg max-w-md shadow-card">
          <h2 className="text-xl font-bold text-fg mb-2">Access Denied</h2>
          <p className="text-fg-secondary">Only platform superusers can access this dashboard.</p>
        </div>
      </div>
    );
  }
  return <>{children}</>;
};

// Admin route: in production require admin subdomain; wrap with AdminProtectedRoute (redirects to /admin/login)
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname.toLowerCase();
    const isProductionMainDomain = hostname === 'launchpointapp.com' || hostname === 'www.launchpointapp.com';
    const isAdminHost = hostname === ADMIN_HOST.split(':')[0].toLowerCase();
    if (isProductionMainDomain && !isAdminHost) {
      window.location.replace(`https://${ADMIN_HOST}/admin`);
      return (
        <div className="min-h-screen flex items-center justify-center bg-app">
          <div className="text-lg text-fg-muted">Redirecting to Admin Dashboard...</div>
        </div>
      );
    }
  }
  return <AdminProtectedRoute>{children}</AdminProtectedRoute>;
};

// Platform Admin host check: /platform/admin must be accessed via admin subdomain; redirect if on main domain
const PlatformAdminHostRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname.toLowerCase();
    const isProductionMainDomain = hostname === 'launchpointapp.com' || hostname === 'www.launchpointapp.com';
    const isAdminHost = hostname === ADMIN_HOST.split(':')[0].toLowerCase();
    if (isProductionMainDomain && !isAdminHost) {
      window.location.replace(`https://${ADMIN_HOST}${window.location.pathname}`);
      return (
        <div className="min-h-screen flex items-center justify-center bg-app">
          <div className="text-lg text-fg-muted">Redirecting to Platform Admin...</div>
        </div>
      );
    }
  }
  return <>{children}</>;
};

// Platform Admin login - redirect to /platform/admin/dashboard when already superuser
const PlatformAdminLoginRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-app">
        <div className="text-lg text-fg-muted">Loading...</div>
      </div>
    );
  }
  if (isAuthenticated && user?.is_superuser) {
    return <Navigate to="/platform/admin/dashboard" replace />;
  }
  return <>{children}</>;
};

// Admin login is public but redirects to /admin when already authenticated as superuser
const AdminLoginRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-app">
        <div className="text-lg text-fg-muted">Loading...</div>
      </div>
    );
  }

  if (isAuthenticated && user?.is_superuser) {
    return <Navigate to="/admin" replace />;
  }

  return <>{children}</>;
};

// When on admin subdomain at root path, redirect to /platform/admin
const AdminRootRedirect: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname.toLowerCase();
    const path = (window.location.pathname || '/').replace(/\/+$/, '') || '/';
    const isAdminHost = hostname === ADMIN_HOST.split(':')[0].toLowerCase();
    if (isAdminHost && (path === '' || path === '/')) {
      window.location.replace(`https://${ADMIN_HOST}/platform/admin`);
      return (
        <div className="min-h-screen flex items-center justify-center bg-app">
          <div className="text-lg text-fg-muted">Redirecting to Platform Admin...</div>
        </div>
      );
    }
  }
  return <>{children}</>;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <EntitlementProvider>
          <ToastProvider>
            <Router>
              <AdminRootRedirect>
              <div className="App">
                <Routes>
            {/* Public routes */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />
            <Route
              path="/register"
              element={
                <PublicRoute>
                  <Register />
                </PublicRoute>
              }
            />
            <Route
              path="/onboarding/vertical"
              element={
                <ProtectedRoute>
                  <OnboardingVerticalPack />
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding/lead-preferences"
              element={
                <ProtectedRoute>
                  <OnboardingLeadPreferences />
                </ProtectedRoute>
              }
            />
            <Route
              path="/change-password"
              element={
                <ProtectedRoute>
                  <ChangePassword />
                </ProtectedRoute>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <PublicRoute>
                  <ForgotPassword />
                </PublicRoute>
              }
            />
            <Route
              path="/reset-password"
              element={
                <PublicRoute>
                  <ResetPassword />
                </PublicRoute>
              }
            />
            <Route
              path="/verify-email"
              element={
                <PublicRoute>
                  <VerifyEmail />
                </PublicRoute>
              }
            />

            {/* Admin routes (superuser only; in production must use admin subdomain) */}
            <Route
              path="/admin/login"
              element={
                <AdminLoginRoute>
                  <AdminLogin />
                </AdminLoginRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              }
            />

            {/* Platform Admin routes (superuser only; /platform/admin/ namespace; must use admin subdomain) */}
            <Route
              path="/platform/admin/login"
              element={
                <PlatformAdminHostRoute>
                  <PlatformAdminLoginRoute>
                    <PlatformAdminLogin />
                  </PlatformAdminLoginRoute>
                </PlatformAdminHostRoute>
              }
            />
            <Route
              path="/platform/admin"
              element={
                <PlatformAdminHostRoute>
                  <PlatformAdminProtectedRoute>
                    <PlatformAdminLayout />
                  </PlatformAdminProtectedRoute>
                </PlatformAdminHostRoute>
              }
            >
              <Route index element={<Navigate to="/platform/admin/dashboard" replace />} />
              <Route path="dashboard" element={<PlatformAdminOverview />} />
              <Route path="plans" element={<PlatformAdminPlans />} />
              <Route path="tenants" element={<PlatformAdminTenants />} />
              <Route path="users" element={<PlatformAdminUsers />} />
              <Route path="websites" element={<PlatformAdminWebsites />} />
              <Route path="leads" element={<PlatformAdminLeads />} />
              <Route path="revenue" element={<PlatformAdminRevenue />} />
              <Route path="health" element={<PlatformAdminHealth />} />
              <Route path="audit-logs" element={<PlatformAdminAuditLogs />} />
              <Route path="integrations" element={<PlatformAdminIntegrations />} />
            </Route>

            {/* Protected routes */}
            <Route 
              path="/" 
              element={
                <ProtectedRoute>
                  <ImpersonationProvider>
                    <Layout />
                  </ImpersonationProvider>
                </ProtectedRoute>
              }
                    >
                      <Route
                        index
                        element={<EssentialsOr essentials={<EssentialsDashboard />} full={<DashboardOrRedirect />} />}
                      />
                      <Route
                        path="leads"
                        element={<EssentialsOr essentials={<ContactsListPage stage="lead" />} full={<Leads />} />}
                      />
                      <Route path="leads/suggestions" element={<DailyLeadSuggestions />} />
                      <Route path="lead-imports" element={<LeadImports />} />
                      <Route path="lead-imports/:batchId" element={<LeadImportDetail />} />
                      <Route path="leads/inbound/:leadId" element={<LeadInboundDetail />} />
                      <Route
                        path="prospects"
                        element={<EssentialsOr essentials={<ContactsListPage stage="prospect" />} full={<Prospects />} />}
                      />
                      <Route
                        path="clients"
                        element={<EssentialsOr essentials={<ContactsListPage stage="client" />} full={<Clients />} />}
                      />
                      <Route path="pipeline/upsells" element={<UpsellPipeline />} />
                      <Route path="tenant" element={<TenantManagement />} />
                      <Route path="settings/website" element={<WebsiteSettings />} />
                      <Route path="settings/website-integration" element={<WebsiteIntegration />} />
                      <Route path="settings/email" element={<EmailSettings />} />
                      <Route path="settings/email/performance" element={<MessagePerformance />} />
                      <Route path="campaigns" element={<Campaigns />} />
                      <Route path="campaigns/:campaignId" element={<CampaignDetail />} />
                      <Route path="sequences" element={<Sequences />} />
                      <Route path="sequences/new" element={<SequenceEditor />} />
                      <Route path="sequences/:sequenceId" element={<SequenceEditor />} />
                      <Route path="chatbot/settings" element={<ChatbotSettings />} />
                      <Route path="chatbot/install" element={<ChatbotInstall />} />
                      <Route path="chatbot/conversations" element={<Navigate to="/chatbot/inbox" replace />} />
                      <Route path="chatbot/inbox" element={<EssentialsOr essentials={<EssentialsInbox />} full={<ChatInbox />} />} />
                      <Route path="chatbot/inbox/:sessionId" element={<ChatInbox />} />
                      <Route path="tasks" element={<EssentialsOr essentials={<EssentialsTasks />} full={<Tasks />} />} />
                      <Route path="chatbot/leads" element={<ChatbotLeads />} />
                    </Route>
                </Routes>
              </div>
              </AdminRootRedirect>
            </Router>
          </ToastProvider>
        </EntitlementProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
